/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Typography, Link, IconButton, TextField } from '@material-ui/core';
import { Stack, Pagination, PaginationItem } from '@mui/material';
import { DataGrid, useGridApiContext, useGridState } from '@mui/x-data-grid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ClearIcon from '@material-ui/icons/Clear';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import SearchIcon from '@material-ui/icons/Search';
import Lottie from 'react-lottie';

import { getCustomerAccessList } from "../../redux/Actions/customer-access.action";
import useStyles from './styles';
import emptyCustomerAccess from '../../assets/lotties/emptyCustomerAccess.json';

function CustomPagination(props) {
    const apiRef = useGridApiContext();
    const [state] = useGridState(apiRef);

    const classes = useStyles();

    useEffect(() => {
        apiRef.current.setPage(props.page);
    }, [props.page, apiRef]);

    return (
        <div className={classes.customPagination}>
            {state.pagination.rowCount ? (
                <div>
                    <span className={classes.showPaginationDesc}>
                        Showing {state.pagination.page * 10 + 1} to {(state.pagination.page + 1) * 10} of {state.pagination.rowCount} entries
                    </span>
                    <Pagination
                        className={classes.root}
                        color="primary"
                        count={state.pagination.pageCount}
                        page={state.pagination.page + 1}
                        onChange={(event, value) => apiRef.current.setPage(value - 1)}
                        ariant="outlined"
                        shape="rounded"
                        hideNextButton={false}
                        variant="outlined"
                        renderItem={(item) => (
                            <PaginationItem {...item} className={classes.pageItem} variant="text" />
                        )}
                    />
                </div>
            ) : (
                ''
            )}
        </div>
    );
}

const SearchToolBar = (props) => {
    const classes = useStyles(props.theme);

    const [isSearchOpen, setSearchOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const { debounceRate } = useSelector((state) => state.constants.settings);

    useEffect(() => {
        const timeoutId = setTimeout(() => props.setTenantName(searchText), debounceRate);

        return () => clearTimeout(timeoutId);
    }, [searchText]);

    return (
        <div className={classes.root}>
            {!isSearchOpen ? (
                <strong>
                    <IconButton
                        aria-haspopup="true"
                        color="inherit"
                        onClick={() => {
                            setSearchOpen(true);
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faSearch}
                            className={classes.tableSearchIcon}
                            classes={{ root: classes.headerIcon }}
                        />
                    </IconButton>
                    Customer Details
                </strong>
            ) : (
                <TextField
                    variant="standard"
                    value={searchText}
                    onChange={(e) => {
                        setSearchText(e.target.value);
                    }}
                    placeholder="Search"
                    className={classes.searchTextField}
                    autoFocus
                    InputProps={{
                        classes: {
                            input: classes.resize
                        },
                        startAdornment: <SearchIcon fontSize="small" />,
                        endAdornment: (
                            <IconButton
                                title="Clear"
                                aria-label="Clear"
                                size="small"
                                onClick={() => {
                                    setSearchText('');
                                    setSearchOpen(false);
                                }}
                            >
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        )
                    }}
                />
            )}
        </div>
    );
};

export default function DataTable() {
    const dispatch = useDispatch();
    const classes = useStyles();
    const resultsPerPage = 10;

    const [pagination, setPagination] = useState(0);
    const [tenantName, setTenantName] = useState('');

    const { customerAccessData, count, loadingCustomerAccess } = useSelector((state) => state.customerAccessReducer);

    const detailRowComp = (params) =>
        <div className={classes.customerDetailsContainer}>
            <Link href={params.row.adminUrl} target="_blank" rel="noopener" className={`${classes.row} ${classes.clientName}`}>{params.row.clientName}</Link>
            <div className={classes.tenantNameContainer}>
                <p className={classes.tenantName}>{params.row.tenantName}</p>
            </div>
        </div>
    const supportTeamComp = (params) =>
        <div className={classes.row}>
            <div>
                {
                    params.row.supportTeamNames.length > 50 ?
                        `${params.row.supportTeamNames.slice(0, 50)}...` :
                        params.row.supportTeamNames
                }
            </div>
        </div>
    const expiryRowComp = (params) =>
        <div className={classes.row}>
            <div>{format(new Date(params.row.expiry), 'd-MM-y')}</div>
        </div>
    const registrationLinkComp = (params) =>
        <div className={classes.row}>
            <Link
                className={classes.registrationLink}
                target="_blank"
                rel="noopener"
                href={params.row.registrationLink}
                underline="always"
            >
                Link
            </Link>
        </div>

    const columns = [
        {
            field: '_id',
            headerName: 'Customer Details',
            minWidth: 200,
            sortable: false,
            flex: 1,
            renderCell: detailRowComp,
            renderHeader: () => (
                <SearchToolBar setTenantName={setTenantName} />
            ),
        },
        {
            field: 'supportTeamNames',
            headerName: 'Support Team',
            sortable: false,
            flex: 3,
            minWidth: 150,
            renderCell: supportTeamComp
        },
        {
            field: 'expiry',
            headerName: 'Expiration Date',
            minWidth: 210,
            sortIcon: { opacity: '0.6 !important' },
            renderCell: expiryRowComp,
            flex: 2
        },
        {
            field: 'registrationLink',
            headerName: 'Registration Link',
            minWidth: 109,
            renderCell: registrationLinkComp,
            sortable: false,
            flex: 1
        },
    ];

    useEffect(() => {
        const query = {
            pageNo: pagination,
            resultsPerPage,
            tenantName,
        };

        dispatch(
            getCustomerAccessList(query)
        );
    }, [pagination, tenantName]);

    return (
        <div className={classes.customerAccessContainer}>
            <Grid container>
                <Typography variant="h1" className={classes.heading}>
                    Customer List with Admin Panel Access
                </Typography>
            </Grid>
            <Stack>
                <DataGrid
                    className={classes.table}
                    classes={{ virtualScrollerRenderZone: customerAccessData.length === 0 && classes.virtualScroller }}
                    rows={customerAccessData}
                    columns={columns}
                    getRowId={(row) => row._id}
                    autoHeight
                    rowCount={count}
                    pageSize={resultsPerPage}
                    pagination
                    hideFooter={count <= resultsPerPage}
                    components={{
                        Pagination: CustomPagination,
                        NoRowsOverlay: () => (
                            <Stack
                                height="100%"
                                alignItems="center"
                                justifyContent="center"
                                className={classes.emptyDataText}
                            >
                                <Lottie height={200} width={200} options={{ animationData: emptyCustomerAccess }} />
                                No Data To Show
                            </Stack>
                        ),
                        NoResultsOverlay: () => (
                            <Stack
                                height="100%"
                                alignItems="center"
                                justifyContent="center"
                                className={classes.emptyDataText}
                            >
                                <Lottie height={200} width={200} options={{ animationData: emptyCustomerAccess }} />
                                No Data To Show
                            </Stack>
                        )
                    }}
                    componentsProps={{
                        pagination: {
                            page: pagination,
                        }
                    }}
                    filterMode="server"
                    paginationMode="server"
                    onPageChange={(newPage) => setPagination(newPage)}
                    loading={loadingCustomerAccess}
                    disableColumnMenu
                />
            </Stack>
        </div>
    );
};